<script setup lang="ts">
/**
 * A document the assistant produced, offered for download.
 *
 * Rendered as its own card next to the answer rather than inside the
 * collapsible tool round-trip: a file the user asked for is the outcome of
 * the turn, not diagnostic detail about how the turn ran. Burying it one
 * click deep is how people miss that it exists at all.
 *
 * The backend stores a spec and renders on request, so format and the
 * anonymised variant are chosen HERE, at download time. That is why the
 * toggle sits on the card and not in the prompt the user had to write.
 */
import { computed, ref } from "vue";

import { downloadGeneratedDocument } from "../api/conversations";
import BrandSquare from "./BrandSquare.vue";

const props = defineProps<{
  conversationId?: number;
  payload: Record<string, unknown>;
  devUser?: string;
}>();

const title = computed(() => String(props.payload.title ?? "Dokument"));
const documentId = computed(() => String(props.payload.document_id ?? ""));
const format = computed(() => String(props.payload.format ?? "docx"));
const kindLabel = computed(() => {
  if (props.payload.kind === "cv") return "CV";
  if (props.payload.kind === "deck") return "Præsentation";
  if (props.payload.kind === "filled") return "Udfyldt skabelon";
  if (props.payload.kind === "service_pitch") return "Salgspitch";
  return "Dokument";
});
/** Only shown where it does something. An inert checkbox on a report would
 *  be worse than none: someone ticks it and believes it was anonymised. */
const canAnonymise = computed(() => props.payload.supports_anonymise === true);
/** Documents the one spec can be downloaded as — a pitch's presentation and
 *  one-pager. Each gets its own button: both are wanted, usually together. */
const variants = computed(() =>
  Array.isArray(props.payload.variants)
    ? (props.payload.variants as { id: string; label: string }[])
    : [],
);
/** Fields left as TBD. Shown on the card so a draft is not mistaken for a
 *  finished pitch before anyone opens it. */
const openItems = computed(() =>
  Array.isArray(props.payload.open_items) ? props.payload.open_items.length : 0,
);

const anonymise = ref(false);
const busy = ref<string | false>(false);
const error = ref<string | null>(null);

const ready = computed(
  () => !!documentId.value && typeof props.conversationId === "number",
);

async function download(variant = ""): Promise<void> {
  if (!ready.value || busy.value) return;
  busy.value = variant || "default";
  error.value = null;
  try {
    error.value = await downloadGeneratedDocument(
      props.conversationId as number,
      documentId.value,
      {
        format: format.value,
        anonymise: canAnonymise.value && anonymise.value,
        variant: variant || undefined,
        devUser: props.devUser,
      },
    );
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="doc-card">
    <BrandSquare size="1.1rem" />
    <div class="doc-card__body">
      <p class="doc-card__title">{{ title }}</p>
      <p class="doc-card__meta">
        {{ kindLabel }} · {{ format.toUpperCase() }}
      </p>
      <label v-if="canAnonymise" class="doc-card__toggle">
        <input v-model="anonymise" type="checkbox" />
        Uden personlige oplysninger
      </label>
      <p v-if="openItems" class="doc-card__open">
        Udkast · {{ openItems }} åbne punkter (TBD)
      </p>
      <p v-if="error" class="doc-card__error" role="alert">{{ error }}</p>
    </div>
    <div v-if="variants.length" class="doc-card__variants">
      <button
        v-for="v in variants"
        :key="v.id"
        type="button"
        class="doc-card__btn"
        :disabled="!ready || !!busy"
        @click="download(v.id)"
      >
        {{ busy === v.id ? "Henter…" : v.label }}
      </button>
    </div>
    <button
      v-else
      type="button"
      class="doc-card__btn"
      :disabled="!ready || !!busy"
      @click="download()"
    >
      {{ busy ? "Henter…" : "Download" }}
    </button>
  </div>
</template>

<style scoped>
.doc-card {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  margin: var(--space-3) 0;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  /* Square corners and a highlight rule: this is a Danoffice IT document,
     and the square is the brand's own graphic element. */
  border-left: 3px solid var(--color-highlight);
  background: var(--color-surface);
}
.doc-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.doc-card__title {
  margin: 0;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.doc-card__meta {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.doc-card__variants {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2, 0.5rem);
  justify-content: flex-end;
}
.doc-card__open {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-warn-ink);
  background: var(--color-warn-bg);
  padding: 0.1rem 0.4rem;
  align-self: flex-start;
}
.doc-card__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.25rem;
  font-size: 0.82rem;
  color: var(--color-muted);
  cursor: pointer;
}
.doc-card__error {
  margin: 0.25rem 0 0;
  font-size: 0.82rem;
  color: var(--color-error);
}
.doc-card__btn {
  flex: 0 0 auto;
  padding: 0.45rem 1rem;
  border: 0;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: var(--color-inverse);
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.doc-card__btn:hover:not(:disabled) {
  background: var(--color-accent-hover);
}
.doc-card__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
@media (max-width: 640px) {
  /* The button goes full width rather than being squeezed against the
     edge — the same rule the admin toolbars needed. */
  .doc-card {
    flex-wrap: wrap;
  }
  .doc-card__btn {
    flex: 1 0 100%;
  }
}
</style>
